import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  MenuItem,
  Stack,
  StatusChip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  iconSize,
  statusTone,
} from '@munifocus/ui';
import {
  unansweredItems,
  type InspectionChecklistItem,
  type InspectionItemResult,
  type InspectionView,
} from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { useAuth } from '../../providers/AuthProvider';
import { useScope } from '../../providers/ScopeProvider';
import { formatDateTime } from '../../lib/relative-time';
import { useMutationToast } from '../../lib/mutation-toast';
import {
  downloadInspectionReport,
  useActiveMunicipality,
  useInspection,
  useInspectionAction,
  useInspectors,
  useUploadInspectionPhoto,
} from '../../lib/permits-api';
import { toLocalInput } from './ScheduleInspectionDialog';

const RESULTS: InspectionItemResult[] = ['pass', 'fail', 'na'];

/** Items in order, grouped under their section headings. */
export function groupBySection(
  items: InspectionChecklistItem[],
  fr: boolean,
): Array<{ section: string | null; items: InspectionChecklistItem[] }> {
  const groups: Array<{ section: string | null; items: InspectionChecklistItem[] }> = [];
  for (const item of items) {
    const section = (fr ? item.sectionFr : item.sectionEn) || null;
    const last = groups[groups.length - 1];
    if (last && last.section === section) last.items.push(item);
    else groups.push({ section, items: [item] });
  }
  return groups;
}

/**
 * One inspection: carry it out at the desk (the field app is the usual place),
 * see the results and photos, complete it, refer a failure to enforcement and
 * download the report in either language.
 */
export function InspectionDetail() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { can } = useScope();
  const { user } = useAuth();
  const toast = useMutationToast();
  const lg = useActiveMunicipality();
  const query = useInspection(lg, id);
  const act = useInspectionAction(lg, id);
  const [summary, setSummary] = useState('');
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const ins = query.data;
  const fr = i18n.language.startsWith('fr');

  // Reset the draft when the stored summary changes (another tab, a save).
  const storedSummary = ins?.summary ?? '';
  useEffect(() => setSummary(storedSummary), [storedSummary]);

  const back = { label: t('gov.inspections.title'), onClick: () => navigate('/inspections') };
  if (query.isLoading || !lg) {
    return (
      <AppShell back={back} title={t('gov.inspections.detail.title')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </AppShell>
    );
  }
  if (!ins) {
    return (
      <AppShell back={back} title={t('gov.inspections.detail.title')}>
        <Alert severity="warning">{t('gov.inspections.detail.notFound')}</Alert>
      </AppShell>
    );
  }

  const isOpen = ins.status === 'scheduled' || ins.status === 'in_progress';
  const scheduler = can('inspection:schedule');
  const mayRecord =
    isOpen && can('inspection:perform') && (ins.inspectorId === user?.id || scheduler);
  const open = unansweredItems(ins.items, ins.results);

  const record = async (
    itemKey: string,
    result: InspectionItemResult | null,
    note?: string | null,
  ) => {
    const current = ins.results[itemKey];
    try {
      await act.mutateAsync({
        action: 'results',
        body: {
          results: [
            {
              itemKey,
              result,
              note: note === undefined ? (current?.note ?? null) : note,
              recordedAt: new Date().toISOString(),
            },
          ],
        },
      });
    } catch (err) {
      toast.error(err, 'gov.inspections.errors.save');
    }
  };

  const doAction = async (input: Parameters<typeof act.mutateAsync>[0], successKey: string) => {
    try {
      await act.mutateAsync(input);
      toast.success(successKey);
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
    }
  };

  const report = (locale: 'en' | 'fr') =>
    downloadInspectionReport(lg, ins.id, ins.reference, locale).catch((err) =>
      toast.error(err, 'gov.permits.errors.download'),
    );

  return (
    <AppShell back={back} title={ins.reference}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h1" sx={{ fontSize: 24 }}>
              {fr ? ins.templateNameFr : ins.templateNameEn}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ins.propertyAddress} · {formatDateTime(ins.scheduledFor, i18n.language)} ·{' '}
              {ins.inspectorName ?? t('gov.inspections.unassigned')}
            </Typography>
            {(ins.permitApplicationReference || ins.fileReference) && (
              <Typography variant="body2" color="text.secondary">
                {ins.permitApplicationReference && (
                  <Box
                    component="button"
                    onClick={() => navigate(`/permits/applications/${ins.permitApplicationId}`)}
                    sx={{
                      border: 0,
                      background: 'none',
                      p: 0,
                      color: 'primary.main',
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                  >
                    {ins.permitApplicationReference}
                    {ins.permitTypeNameEn
                      ? ` · ${fr ? ins.permitTypeNameFr : ins.permitTypeNameEn}`
                      : ''}
                  </Box>
                )}
                {ins.fileReference && (
                  <Box
                    component="button"
                    onClick={() => navigate(`/files/${ins.fileId}`)}
                    sx={{
                      border: 0,
                      background: 'none',
                      p: 0,
                      color: 'primary.main',
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                  >
                    {ins.fileReference}
                  </Box>
                )}
              </Typography>
            )}
          </Box>
          <StatusChip
            status={
              ins.status === 'completed' && ins.outcome
                ? statusTone.inspectionResult[ins.outcome]
                : statusTone.inspection[ins.status]
            }
            label={
              ins.status === 'completed' && ins.outcome
                ? t(`gov.inspections.outcomes.${ins.outcome}`)
                : t(`gov.inspections.statuses.${ins.status}`)
            }
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {(['en', 'fr'] as const).map((locale) => (
            <Button
              key={locale}
              variant="outlined"
              startIcon={<Icons.Download size={iconSize.sm} />}
              onClick={() => void report(locale)}
            >
              {t(locale === 'en' ? 'gov.inspections.reportEn' : 'gov.inspections.reportFr')}
            </Button>
          ))}
          {scheduler && isOpen && (
            <>
              <Button variant="outlined" onClick={() => setEditing(true)}>
                {t('gov.inspections.detail.reschedule')}
              </Button>
              <Button color="error" onClick={() => setCancelling(true)}>
                {t('gov.inspections.detail.cancel')}
              </Button>
            </>
          )}
          {ins.status === 'completed' &&
            ins.outcome === 'fail' &&
            !ins.complaintReference &&
            can('inspection:perform') && (
              <Button
                variant="contained"
                color="error"
                disabled={act.isPending}
                onClick={() =>
                  void doAction({ action: 'refer' }, 'gov.inspections.detail.referred')
                }
              >
                {t('gov.inspections.detail.refer')}
              </Button>
            )}
        </Stack>

        {ins.complaintReference && (
          <Alert severity="info">
            {t('gov.inspections.detail.referredAs', { ref: ins.complaintReference })}
          </Alert>
        )}
        {ins.status === 'cancelled' && (
          <Alert severity="info">
            {t('gov.inspections.detail.cancelledBecause')} {ins.cancelledReason}
          </Alert>
        )}
        {isOpen && !mayRecord && (
          <Alert severity="info">{t('gov.inspections.detail.notYours')}</Alert>
        )}

        {groupBySection(ins.items, fr).map((group, gi) => (
          <HairlineCard key={gi} sx={{ p: 2 }}>
            {group.section && (
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                {group.section}
              </Typography>
            )}
            <Stack spacing={2}>
              {group.items.map((item) => (
                <ItemRow
                  key={item.key}
                  inspection={ins}
                  item={item}
                  fr={fr}
                  editable={mayRecord}
                  onResult={(r) => void record(item.key, r)}
                  onNote={(note) =>
                    void record(item.key, ins.results[item.key]?.result ?? null, note)
                  }
                />
              ))}
            </Stack>
          </HairlineCard>
        ))}

        <HairlineCard sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">{t('gov.inspections.detail.summary')}</Typography>
            <TextField
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              onBlur={() => {
                if (mayRecord && summary !== (ins.summary ?? '')) {
                  void act
                    .mutateAsync({ action: 'results', body: { results: [], summary } })
                    .catch((err) => toast.error(err, 'gov.inspections.errors.save'));
                }
              }}
              multiline
              minRows={3}
              disabled={!mayRecord}
              fullWidth
              placeholder={t('gov.inspections.detail.summaryPlaceholder')}
            />
            {mayRecord && (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {open.length
                    ? t('gov.inspections.detail.remaining', { count: open.length })
                    : t('gov.inspections.detail.allAnswered')}
                </Typography>
                <Button
                  variant="contained"
                  disabled={open.length > 0 || act.isPending}
                  onClick={() =>
                    void doAction(
                      { action: 'complete', body: { summary } },
                      'gov.inspections.detail.completed',
                    )
                  }
                >
                  {t('gov.inspections.detail.complete')}
                </Button>
              </Stack>
            )}
          </Stack>
        </HairlineCard>
      </Stack>

      {editing && (
        <RescheduleDialog
          lg={lg}
          inspection={ins}
          onClose={() => setEditing(false)}
          onSave={(body) =>
            doAction({ action: 'update', body }, 'gov.inspections.detail.rescheduled').then(() =>
              setEditing(false),
            )
          }
        />
      )}
      {cancelling && (
        <CancelDialog
          onClose={() => setCancelling(false)}
          onConfirm={(reason) =>
            doAction(
              { action: 'cancel', body: { reason } },
              'gov.inspections.detail.cancelled',
            ).then(() => setCancelling(false))
          }
        />
      )}
    </AppShell>
  );
}

function ItemRow({
  inspection,
  item,
  fr,
  editable,
  onResult,
  onNote,
}: {
  inspection: InspectionView;
  item: InspectionChecklistItem;
  fr: boolean;
  editable: boolean;
  onResult: (r: InspectionItemResult) => void;
  onNote: (note: string | null) => void;
}) {
  const { t } = useTranslation();
  const toast = useMutationToast();
  const lg = useActiveMunicipality();
  const upload = useUploadInspectionPhoto(lg, inspection.id);
  const fileInput = useRef<HTMLInputElement>(null);
  const record = inspection.results[item.key];
  const [note, setNote] = useState(record?.note ?? '');
  const photos = inspection.photos.filter((p) => p.itemKey === item.key);
  const guidance = fr ? item.guidanceFr : item.guidanceEn;

  useEffect(() => setNote(record?.note ?? ''), [record?.note]);

  const icon: Record<InspectionItemResult, ReactNode> = {
    pass: <Icons.Check size={iconSize.sm} />,
    fail: <Icons.X size={iconSize.sm} />,
    na: <Icons.Minus size={iconSize.sm} />,
  };

  return (
    <Stack spacing={1}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' } }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {fr ? item.labelFr : item.labelEn}
          </Typography>
          {guidance && (
            <Typography variant="caption" color="text.secondary">
              {guidance}
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={record?.result ?? null}
          onChange={(_, v: InspectionItemResult | null) => v && onResult(v)}
          disabled={!editable}
          aria-label={fr ? item.labelFr : item.labelEn}
        >
          {RESULTS.map((r) => (
            <ToggleButton
              key={r}
              value={r}
              color={r === 'fail' ? 'error' : r === 'pass' ? 'success' : 'standard'}
              sx={{ gap: 0.5, px: 1.5 }}
            >
              {icon[r]}
              {t(`gov.inspections.results.${r}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      {(editable || record?.note) && (
        <TextField
          size="small"
          placeholder={t('gov.inspections.detail.notePlaceholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if (editable && note !== (record?.note ?? '')) onNote(note.trim() || null);
          }}
          disabled={!editable}
          multiline
          fullWidth
        />
      )}
      {(photos.length > 0 || editable) && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}
        >
          {photos.map((p) => (
            <Box
              key={p.id}
              component="a"
              href={p.url}
              target="_blank"
              rel="noreferrer"
              sx={{
                display: 'block',
                width: 72,
                height: 72,
                borderRadius: 1,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Box
                component="img"
                src={p.url}
                alt={t('gov.inspections.detail.photoAlt', {
                  item: fr ? item.labelFr : item.labelEn,
                })}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          ))}
          {editable && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  try {
                    await upload.mutateAsync({ itemKey: item.key, file });
                  } catch (err) {
                    toast.error(err, 'gov.inspections.errors.photo');
                  }
                }}
              />
              <Button
                size="small"
                startIcon={<Icons.Camera size={iconSize.sm} />}
                disabled={upload.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {t('gov.inspections.detail.addPhoto')}
              </Button>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function RescheduleDialog({
  lg,
  inspection,
  onClose,
  onSave,
}: {
  lg: string;
  inspection: InspectionView;
  onClose: () => void;
  onSave: (body: { scheduledFor: string; inspectorId: string | null }) => void;
}) {
  const { t } = useTranslation();
  const inspectors = useInspectors(lg);
  const [when, setWhen] = useState(toLocalInput(new Date(inspection.scheduledFor)));
  const [inspectorId, setInspectorId] = useState(inspection.inspectorId ?? '');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.inspections.detail.reschedule')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            type="datetime-local"
            label={t('gov.inspections.fields.when')}
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <TextField
            select
            label={t('gov.inspections.fields.inspector')}
            value={inspectorId}
            onChange={(e) => setInspectorId(e.target.value)}
            fullWidth
          >
            <MenuItem value="">{t('gov.inspections.unassigned')}</MenuItem>
            {(inspectors.data ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!when}
          onClick={() =>
            onSave({ scheduledFor: new Date(when).toISOString(), inspectorId: inspectorId || null })
          }
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CancelDialog({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.inspections.detail.cancelTitle')}</DialogTitle>
      <DialogContent>
        <TextField
          sx={{ mt: 1 }}
          label={t('gov.inspections.detail.cancelReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
          required
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.back')}</Button>
        <Button
          color="error"
          variant="contained"
          disabled={reason.trim().length < 3}
          onClick={() => onConfirm(reason.trim())}
        >
          {t('gov.inspections.detail.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
